// Transform module. Exports the composable op array (`transformOps`)
// consumed by `createOpTable([...builtins, ...transformOps])`.
//
// Internal helpers (`AggSpec`, `aggContextScope`, `bucketTimestamp`,
// `compileAggPair`, …) are imported directly from their source files;
// this module exposes only the composable op array plus the
// agg-context scope so the chat-side renderer can list agg-context funcs.

import { aggContextScope } from './agg-funcs.ts';
import { aggregate } from './aggregate.ts';
import { arrayFuncs } from './array-funcs.ts';
import { classify } from './classify.ts';
import { columns } from './columns.ts';
import { derive } from './derive.ts';
import { distinct } from './distinct.ts';
import { filter } from './filter.ts';
import { fold } from './fold.ts';
import { fuzzyMatch } from './fuzzy-match.ts';
import { join } from './join.ts';
import { limit } from './limit.ts';
import { pivot } from './pivot.ts';
import { rename } from './rename.ts';
import { dateBucket, formatDate, toDate } from './scalar-funcs.ts';
import { sort } from './sort.ts';
import { spread } from './spread.ts';
import { stringFuncs } from './string-funcs.ts';
import { timeBucket } from './time-bucket.ts';
import { unnest } from './unnest.ts';
import { unroll } from './unroll.ts';
import {
  backwardFill,
  cumIntegral,
  cummax,
  cummin,
  cumsum,
  delta,
  ewma,
  firstValue,
  forwardFill,
  lag,
  lastValue,
  lead,
  pctChange,
  rateOfChange,
  rollingMax,
  rollingMean,
  rollingMin,
  rollingSlope,
  rollingStddev,
  rollingSum,
} from './window.ts';

/** Top-level transform ops — every transform-module op the engine exposes. */
export const transformOps = [
  filter,
  derive,
  sort,
  limit,
  fuzzyMatch,
  classify,
  columns,
  distinct,
  rename,
  fold,
  spread,
  unroll,
  unnest,
  pivot,
  aggregate,
  timeBucket,
  join,
  forwardFill,
  backwardFill,
  lag,
  lead,
  firstValue,
  lastValue,
  rollingMean,
  rollingSum,
  rollingMin,
  rollingMax,
  rollingStddev,
  rollingSlope,
  ewma,
  cumsum,
  cumIntegral,
  cummax,
  cummin,
  delta,
  pctChange,
  rateOfChange,
  toDate,
  dateBucket,
  formatDate,
  ...stringFuncs,
  ...arrayFuncs,
] as const;

/** Re-export the agg-context scope for doc-walker registration on parent verbs. */
export { aggContextScope };
